import { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';

const DEFAULT_MARGIN = Object.freeze({
  left: 40,
  right: 120,
  top: 20,
  bottom: 40,
});

const EMPTY_OFFSETS = Object.freeze({});

const defaultLegendOffset = ({ containerWidth }) => ({
  x: containerWidth - 300,
  y: -10,
});

const getJitter = (range = 0) => (Math.random() - 0.5) * range;

const resolveMarkerY = (marker, height, laneOffsets) => {
  if (typeof marker.yOffset === 'number') return marker.yOffset;
  if (marker.lane && typeof laneOffsets[marker.lane] === 'number') {
    return laneOffsets[marker.lane];
  }
  return height - 10;
};

const VASLinearPlot = ({
  markers = [],
  legendItems = [],
  ariaLabel = 'VAS linear plot',
  height = 70,
  svgHeight = '150px',
  initialWidth = 800,
  margin = DEFAULT_MARGIN,
  laneOffsets = EMPTY_OFFSETS,
  legendOffset = defaultLegendOffset,
}) => {
  const svgRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(initialWidth);

  const resolvedMargin = useMemo(() => ({
    ...DEFAULT_MARGIN,
    ...margin,
  }), [margin]);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (entries[0]?.contentRect?.width) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    if (svgRef.current?.parentElement) {
      observer.observe(svgRef.current.parentElement);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current) return;

    const width = containerWidth - resolvedMargin.left - resolvedMargin.right;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg
      .attr('viewBox', `0 0 ${containerWidth} ${height + resolvedMargin.top + resolvedMargin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('overflow', 'visible');

    const chart = svg.append('g')
      .attr('transform', `translate(${resolvedMargin.left},${resolvedMargin.top})`);

    const xScale = d3.scaleLinear().domain([0, 100]).range([0, width]);

    chart.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).ticks(5).tickFormat(d => `${d}`))
      .selectAll('text')
      .style('font-size', '12px')
      .style('fill', '#333');

    markers.forEach(marker => {
      const circle = chart.append('circle')
        .attr('cx', xScale(marker.value))
        .attr('cy', resolveMarkerY(marker, height, laneOffsets) + getJitter(marker.jitter ?? 0))
        .attr('r', marker.radius ?? 5)
        .style('fill', marker.fill ?? 'none')
        .style('stroke', marker.stroke ?? marker.fill ?? '#fff')
        .style('stroke-width', marker.strokeWidth ?? 1);

      if (marker.title) {
        circle.append('title').text(marker.title);
      }
    });

    const offset = legendOffset({
      containerWidth,
      height,
      margin: resolvedMargin,
    });

    const legend = svg.append('g')
      .attr('transform', `translate(${offset.x}, ${offset.y})`);

    legend.selectAll('circle')
      .data(legendItems)
      .enter()
      .append('circle')
      .attr('cx', 6)
      .attr('cy', (_, index) => index * 20 + 6)
      .attr('r', item => item.marker?.radius ?? 5)
      .style('fill', item => item.marker?.fill ?? 'none')
      .style('stroke', item => item.marker?.stroke ?? item.marker?.fill ?? '#fff')
      .style('stroke-width', item => item.marker?.strokeWidth ?? 2);

    legend.selectAll('text')
      .data(legendItems)
      .enter()
      .append('text')
      .attr('x', 20)
      .attr('y', (_, index) => index * 20 + 10)
      .text(item => item.label)
      .style('font-size', '12px');
  }, [
    containerWidth,
    height,
    laneOffsets,
    legendItems,
    legendOffset,
    markers,
    resolvedMargin,
  ]);

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <svg
        ref={svgRef}
        aria-label={ariaLabel}
        role="img"
        width="100%"
        height={svgHeight}
      />
    </div>
  );
};

export default VASLinearPlot;
